/**
 * Tree Multi-Select Component
 *
 * Renders a hierarchical multi-select control. Each instance is a DOM element
 * marked with `data-tree-select` containing items shaped like:
 *   .tree-select-item[data-tree-id][data-tree-parent-id][data-tree-name][data-tree-path]
 *
 * Items can be checked, the tree can be collapsed/expanded, and selections are
 * shown as breadcrumb chips at the top of the widget. Widgets with the
 * `tree-select-locked` class are read-only: no handlers are wired up.
 */

namespace MSTrgovina.Common.TreeMultiSelect {

    const INIT_FLAG: string = "treeMultiSelectInit";

    /**
     * Initialize every uninitialized widget on the page.
     * Safe to call repeatedly (e.g. after AJAX form replacement).
     */
    export function init(): void {
        $("[data-tree-select]").each(function (): void {
            const $widget: JQuery = $(this);
            if ($widget.data(INIT_FLAG) === true) {
                return;
            }
            $widget.data(INIT_FLAG, true);

            // Read-only widget — no UI interaction wired up
            if ($widget.hasClass("tree-select-locked")) {
                return;
            }

            initWidget($widget);
        });
    }

    function initWidget($widget: JQuery): void {
        // Mark initially checked rows + expand their ancestors so selections are visible
        $widget.find(".tree-select-checkbox:checked").each(function (): void {
            const $item: JQuery = $(this).closest(".tree-select-item");
            $item.addClass("is-selected");
            expandAncestors($widget, $item);
        });

        updateCount($widget);
        updateSelectedPanel($widget);
        updateVisibility($widget);

        // Expand/collapse on chevron click
        $widget.on("click", ".tree-select-toggle", function (): void {
            const $item: JQuery = $(this).closest(".tree-select-item");
            if (!$item.hasClass("has-children")) {
                return;
            }
            $item.toggleClass("collapsed").toggleClass("expanded");
            updateVisibility($widget);
        });

        // Checkbox change → recount + repaint chips + highlight row
        $widget.on("change", ".tree-select-checkbox", function (): void {
            const $item: JQuery = $(this).closest(".tree-select-item");
            $item.toggleClass("is-selected", (this as HTMLInputElement).checked);
            updateCount($widget);
            updateSelectedPanel($widget);
        });

        // Remove a single chip
        $widget.on("click", ".tree-select-chip-remove", function (e: JQuery.ClickEvent): void {
            e.preventDefault();
            const id: string = ($(this).closest(".tree-select-chip").attr("data-tree-id") || "").toString();
            $widget.find(".tree-select-checkbox[value='" + id + "']").prop("checked", false).trigger("change");
        });

        // Clear all selections
        $widget.on("click", ".tree-select-clear-all", function (e: JQuery.ClickEvent): void {
            e.preventDefault();
            $widget.find(".tree-select-checkbox:checked").prop("checked", false);
            $widget.find(".tree-select-item.is-selected").removeClass("is-selected");
            updateCount($widget);
            updateSelectedPanel($widget);
        });

        // Live search filter
        $widget.on("input", ".tree-select-search", function (): void {
            updateVisibility($widget);
        });

        // Expand all / collapse all
        $widget.on("click", ".tree-select-expand-all", function (e: JQuery.ClickEvent): void {
            e.preventDefault();
            $widget.find(".tree-select-item.has-children").removeClass("collapsed").addClass("expanded");
            updateVisibility($widget);
        });
        $widget.on("click", ".tree-select-collapse-all", function (e: JQuery.ClickEvent): void {
            e.preventDefault();
            $widget.find(".tree-select-item.has-children").removeClass("expanded").addClass("collapsed");
            updateVisibility($widget);
        });
    }

    function expandAncestors($widget: JQuery, $item: JQuery): void {
        const parentId: string = ($item.attr("data-tree-parent-id") || "").toString();
        if (!parentId) {
            return;
        }
        const $parent: JQuery = $widget.find(".tree-select-item[data-tree-id='" + parentId + "']");
        if ($parent.length === 0) {
            return;
        }
        $parent.removeClass("collapsed").addClass("expanded");
        expandAncestors($widget, $parent);
    }

    function updateCount($widget: JQuery): void {
        const count: number = $widget.find(".tree-select-checkbox:checked").length;
        $widget.find(".tree-select-count").text(count.toString());
    }

    function updateSelectedPanel($widget: JQuery): void {
        const $panel: JQuery = $widget.find(".tree-select-selected");
        if ($panel.length === 0) {
            return;
        }
        const $checked: JQuery = $widget.find(".tree-select-checkbox:checked");

        if ($checked.length === 0) {
            const emptyMessage: string = $panel.attr("data-empty-message") || "";
            $panel.html("<span class=\"tree-select-empty text-muted small\">" + escapeHtml(emptyMessage) + "</span>");
            return;
        }

        const removeLabel: string = $panel.attr("data-remove-label") || "";
        const chips: string[] = [];
        $checked.each(function (): void {
            const $item: JQuery = $(this).closest(".tree-select-item");
            const id: string = ($item.attr("data-tree-id") || "").toString();
            const path: string = ($item.attr("data-tree-path") || "").toString();
            chips.push(
                "<span class=\"tree-select-chip\" data-tree-id=\"" + escapeHtml(id) + "\">"
                + "<span class=\"tree-select-chip-label\">" + escapeHtml(path) + "</span>"
                + "<button type=\"button\" class=\"tree-select-chip-remove\""
                + " title=\"" + escapeHtml(removeLabel) + "\""
                + " aria-label=\"" + escapeHtml(removeLabel) + "\">&times;</button>"
                + "</span>"
            );
        });
        $panel.html(chips.join(""));
    }

    function updateVisibility($widget: JQuery): void {
        const searchTerm: string = (($widget.find(".tree-select-search").val() as string) || "").toLowerCase().trim();
        const $items: JQuery = $widget.find(".tree-select-item");

        // When searching: collect matching items + all their ancestors so context is shown
        const matchingIds: { [id: string]: boolean } = {};
        if (searchTerm) {
            $items.each(function (): void {
                const $it: JQuery = $(this);
                const name: string = ($it.attr("data-tree-name") || "").toString();
                if (name.indexOf(searchTerm) >= 0) {
                    let $cur: JQuery = $it;
                    while ($cur.length > 0) {
                        const id: string = ($cur.attr("data-tree-id") || "").toString();
                        matchingIds[id] = true;
                        const parentId: string = ($cur.attr("data-tree-parent-id") || "").toString();
                        if (!parentId) {
                            break;
                        }
                        $cur = $widget.find(".tree-select-item[data-tree-id='" + parentId + "']");
                    }
                }
            });
        }

        $items.each(function (): void {
            const $it: JQuery = $(this);
            const id: string = ($it.attr("data-tree-id") || "").toString();

            if (searchTerm && !matchingIds[id]) {
                $it.hide();
                return;
            }

            // Honor collapsed ancestors only when not searching
            let visible: boolean = true;
            if (!searchTerm) {
                let parentId: string = ($it.attr("data-tree-parent-id") || "").toString();
                while (parentId) {
                    const $parent: JQuery = $widget.find(".tree-select-item[data-tree-id='" + parentId + "']");
                    if ($parent.length === 0) {
                        break;
                    }
                    if ($parent.hasClass("collapsed")) {
                        visible = false;
                        break;
                    }
                    parentId = ($parent.attr("data-tree-parent-id") || "").toString();
                }
            }

            if (visible) {
                $it.show();
            } else {
                $it.hide();
            }
        });
    }

    function escapeHtml(value: string): string {
        return value
            .replace(/&/g, "&amp;")
            .replace(/</g, "&lt;")
            .replace(/>/g, "&gt;")
            .replace(/"/g, "&quot;")
            .replace(/'/g, "&#39;");
    }
}

$(function (): void {
    MSTrgovina.Common.TreeMultiSelect.init();
});

$(document).on("mstrgovina:formReplaced", function (): void {
    MSTrgovina.Common.TreeMultiSelect.init();
});
